import { StudyOrderDiagnosisType, StudyOrderItem } from "../../../interface/interface";

type ValidationInput = {
  items: StudyOrderItem[];
  diagnosisType: StudyOrderDiagnosisType | string | null | undefined;
  diagnosis: string | null | undefined;
  diagnosisIcd10Code: string | null | undefined;
  requiresMappedCie10: boolean;
  allowedSourceIds?: number[];
};

const normalizeDiagnosisType = (
  value: StudyOrderDiagnosisType | string | null | undefined
): StudyOrderDiagnosisType => (String(value ?? "").toUpperCase() === "SNOMED" ? "SNOMED" : "NBU");

const normalize = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export const resolveItemSourceId = (item: StudyOrderItem): number | null => {
  const sourceId = Number(item?.nomenclatorSourceId ?? 0);
  if (Number.isFinite(sourceId) && sourceId > 0) {
    return sourceId;
  }
  const nomenclatorId = Number(item?.nomenclatorId ?? 0);
  if (Number.isFinite(nomenclatorId) && nomenclatorId > 0) {
    return 1;
  }
  return null;
};

const getPracticeLabel = (item: StudyOrderItem): string =>
  [item?.code, item?.description].filter(Boolean).join(" - ") || item?.description || "sin descripcion";

export const getFirstInvalidItemMessage = (params: ValidationInput): string | null => {
  const { items, diagnosisType, diagnosis, diagnosisIcd10Code, requiresMappedCie10, allowedSourceIds = [] } = params;
  const normalizedDiagnosisType = normalizeDiagnosisType(diagnosisType);
  const diagnosisTrimmed = String(diagnosis ?? "").trim();

  if (requiresMappedCie10 && normalizedDiagnosisType === "SNOMED" && diagnosisTrimmed && !diagnosisIcd10Code) {
    return "El diagnostico SNOMED seleccionado no tiene mapeo CIE10.";
  }

  const unsupportedItem = items.find(
    (item) =>
      item?.supportedByRepository === false ||
      (typeof item?.unsupportedReason === "string" && item.unsupportedReason.trim().length > 0)
  );
  if (unsupportedItem) {
    const reason = String(unsupportedItem?.unsupportedReason ?? "").trim();
    const label = getPracticeLabel(unsupportedItem);
    return `${reason || "La practica seleccionada no se puede validar con el repositorio/cobertura actual."} (${label})`;
  }

  if (allowedSourceIds.length > 0) {
    const invalidSourceItem = items.find((item) => {
      if (item?.origin === "FREE_TEXT") return false;
      const source = resolveItemSourceId(item);
      return source === null || !allowedSourceIds.includes(source);
    });
    if (invalidSourceItem) {
      const source = resolveItemSourceId(invalidSourceItem);
      return `La practica '${getPracticeLabel(invalidSourceItem)}' tiene una fuente no permitida (${source ?? "sin fuente"}). Permitidas: ${allowedSourceIds.join(", ")}.`;
    }
  }

  return null;
};

export const hasAnyInvalidStudyOrderItems = (params: ValidationInput): boolean =>
  getFirstInvalidItemMessage(params) !== null;

export const buildItemValidationReasonMap = (
  items: StudyOrderItem[],
  allowedSourceIds: number[]
): Map<string, string> => {
  const out = new Map<string, string>();

  items.forEach((item) => {
    const id = String(item?.id ?? "");
    if (!id) return;

    const unsupportedReason = normalize(item?.unsupportedReason);
    if (item?.supportedByRepository === false || unsupportedReason) {
      out.set(
        id,
        String(item?.unsupportedReason ?? "").trim() ||
          "Esta practica no se puede validar con el repositorio/cobertura seleccionada."
      );
      return;
    }

    if (allowedSourceIds.length > 0 && item?.origin !== "FREE_TEXT") {
      const source = resolveItemSourceId(item);
      if (source === null || !allowedSourceIds.includes(source)) {
        out.set(
          id,
          `Fuente ${source ?? "sin fuente"} no permitida. Permitidas: ${allowedSourceIds.join(", ")}.`
        );
      }
    }
  });

  return out;
};
